import { useState } from "react";
import "./user-gateway.scss";
import homeGraphic from "assets/images/HomeGraphic.png";
import { useLocation, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { emailAddress } from "utilities/formSchemas";
import { z } from "zod";
import { Button } from "components/ui/button/Button";
import { FormInput } from "components/ui/form/FormInput";
import { useForm } from "react-hook-form";
import Form from "components/ui/form/Form";
import { useCheckUserAuthStatusMutation } from "pages/authentication/authenticationService";
import { useDispatch } from "react-redux";
import { setEmailAddress, setNhDealApplicationId, setUserId } from "features/authSlice";
import Footer from "components/footer/Footer";

const formSchema = z.object({ email: emailAddress });
type FormData = z.infer<typeof formSchema>;

export default function UserGateway() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const action = queryParams.get("action") ?? "ESign";
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [checkUserAuthStatus, { isLoading }] = useCheckUserAuthStatusMutation();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });

  const onSubmit = async (data: FormData) => {
    setErrorMessage(null);
    try {
      const res = await checkUserAuthStatus({ email: data.email, action }).unwrap();
      if (res.isAuthCompleted) {
        navigate("/", { state: { email: data.email } });
      } else {
        dispatch(setUserId(res.userId ?? ""));
        dispatch(setNhDealApplicationId(res.nhDealApplicationId ?? ""));
        dispatch(setEmailAddress(data.email));
        navigate("/registration");
      }
    } catch (err: unknown) {
      if (typeof err === "object" && err !== null && "status" in err) {
        const status = (err as { status: number }).status;
        if (status === 406) {
          setErrorMessage("Please contact your loan officer to update your contact information");
        } else {
          setErrorMessage("Server error. Please try again later.");
        }
      } else {
        setErrorMessage("Email address not found.");
      }
    }
  };

  return (
    <>
      <div className="user-gateway">
        <div className="user-gateway__content">
          <div className="user-gateway__wrapper">
            <div className="user-gateway__header">Enter the email address you provided us for your application.</div>
            {errorMessage && (
              <div className="user-gateway__error-message">
                <p>{errorMessage}</p>
              </div>
            )}
            <div className="user-gateway__form-wrapper">
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormInput
                  formHandle={register("email")}
                  inputProps={{
                    name: "email",
                    placeholder: "Enter email",
                    label: "Email Address",
                    errorMessage: errors.email?.message,
                  }}
                />
                <Button title="Continue" disabled={!isValid || isLoading} />
              </Form>
            </div>
          </div>
        </div>
        <div className="user-gateway__home-animation mobile-view">
          <img src={homeGraphic} alt="Home graphic" />
        </div>
      </div>
      <div className="user-gateway-footer">
        <Footer />
      </div>
    </>
  );
}